/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback, useMemo, useState} from 'react';
import {MenuProps} from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Divider from '@ion/dls/src/components/divider/Divider';
import {AttributeSuggestionProps} from './types';

/**
 * Renders Single select Atttributes selection dropdown.
 * @param mapping {[key: string]: any}
 * @param listOptions string[]
 * @param onSelection Function
 * @param classes Object
 * @returns JSX
 *
 * ```typescript
 * import AttributeSuggestions from '@ion/features/src/search/AttributeSuggestions';
 *
 * <AttributeSuggestions nSelection={onSelection}
 *   classes={classes}
 *   mapping={attributeMappings}
 *   listOptions={listOptions}/>
 * ```
 */

const ID = 'modularsearch-attribute-selection';

const AttributeSuggestions = ({
  mapping,
  onSelection,
  value,
  classes,
  listOptions
}: AttributeSuggestionProps) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const onFocus = useCallback(function() {
    setIsFocused(true);
  }, []);

  const onBlur = useCallback(function() {
    setIsFocused(false);
  }, []);

  const menuProps = useMemo(() => ({
    anchorOrigin : {
      vertical   : 'bottom',
      horizontal : -4
    },
    transformOrigin : {
      vertical   : 'top',
      horizontal : 'left'
    },
    getContentAnchorEl : null,
    classes            : {
      paper : classes.attributesSelectionMenu
    }
  }), []);

  return (
    <Select disableUnderline
      variant={'standard'}
      autoWidth
      id={ID}
      value={value}
      onClick={onSelection}
      onChange={onSelection}
      onFocus={onFocus}
      onBlur={onBlur}
      classes={{select: classes.attributesSelect}}
      MenuProps={menuProps as Partial<MenuProps>}>
      {listOptions.map((option: string, index: number) => (
        <MenuItem classes={{root: classes.attributesMenuItem}} key={`${option}-${index}`} value={option}>
          {mapping[option]?.label}
        </MenuItem>
      ))}
    </Select>
  );
};

export default AttributeSuggestions;
